/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ScheduleTimeOff } from '../models/ScheduleTimeOff';
import type { ScheduleTimeOffRejectRequest } from '../models/ScheduleTimeOffRejectRequest';
import type { ScheduleTimeOffRequest } from '../models/ScheduleTimeOffRequest';
import type { ScheduleTimeOffUpdateRequest } from '../models/ScheduleTimeOffUpdateRequest';
import { request as __request } from '../core/request';

export class ScheduleTimeOffService {

    /**
     * List time-off requests for a company within a date range
     * @returns ScheduleTimeOff Time-off requests in range, ordered by date then employee
     * @throws ApiError
     */
    public static async getScheduleTimeOffByCompany({
companyId,
startDate,
endDate,
}: {
companyId: number,
startDate: string,
endDate: string,
}): Promise<Array<ScheduleTimeOff>> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/schedule/time-off`,
            query: {
                'startDate': startDate,
                'endDate': endDate,
            },
            errors: {
                400: `Invalid request`,
            },
        });
        return result.body;
    }

    /**
     * Create a time-off request (single date or inclusive date range)
     * @returns ScheduleTimeOff The created time-off request(s), one row per resolved date, status=pending
     * @throws ApiError
     */
    public static async createScheduleTimeOff({
companyId,
requestBody,
}: {
companyId: number,
requestBody: ScheduleTimeOffRequest,
}): Promise<Array<ScheduleTimeOff>> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/schedule/time-off`,
            body: requestBody,
            errors: {
                400: `Invalid request`,
            },
        });
        return result.body;
    }

    /**
     * Update the date/reason on a still-pending time-off request
     * @returns ScheduleTimeOff The updated time-off request
     * @throws ApiError
     */
    public static async updateScheduleTimeOff({
companyId,
scheduleTimeOffId,
requestBody,
}: {
companyId: number,
scheduleTimeOffId: number,
requestBody: ScheduleTimeOffUpdateRequest,
}): Promise<ScheduleTimeOff> {
        const result = await __request({
            method: 'PATCH',
            path: `/companies/${companyId}/schedule/time-off/${scheduleTimeOffId}`,
            body: requestBody,
            errors: {
                400: `Invalid request`,
                404: `No pending request with that id`,
            },
        });
        return result.body;
    }

    /**
     * Soft-delete (archive) a time-off request, regardless of status
     * @returns any Deleted
     * @throws ApiError
     */
    public static async deleteScheduleTimeOff({
companyId,
scheduleTimeOffId,
}: {
companyId: number,
scheduleTimeOffId: number,
}): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/companies/${companyId}/schedule/time-off/${scheduleTimeOffId}`,
            errors: {
                404: `No request with that id`,
            },
        });
        return result.body;
    }

    /**
     * Accept a pending time-off request; releases the employee's schedule for that date
     * @returns ScheduleTimeOff The accepted time-off request
     * @throws ApiError
     */
    public static async acceptScheduleTimeOff({
companyId,
scheduleTimeOffId,
}: {
companyId: number,
scheduleTimeOffId: number,
}): Promise<ScheduleTimeOff> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/schedule/time-off/${scheduleTimeOffId}/accept`,
            errors: {
                404: `No pending request with that id`,
            },
        });
        return result.body;
    }

    /**
     * Reject a pending time-off request with a reason
     * @returns ScheduleTimeOff The rejected time-off request
     * @throws ApiError
     */
    public static async rejectScheduleTimeOff({
companyId,
scheduleTimeOffId,
requestBody,
}: {
companyId: number,
scheduleTimeOffId: number,
requestBody: ScheduleTimeOffRejectRequest,
}): Promise<ScheduleTimeOff> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/schedule/time-off/${scheduleTimeOffId}/reject`,
            body: requestBody,
            errors: {
                404: `No pending request with that id`,
            },
        });
        return result.body;
    }

    /**
     * Withdraw a pending or already-accepted time-off request
     * @returns ScheduleTimeOff The cancelled time-off request. Note: if it had been accepted, this does NOT restore the original schedule assignment.
 * 
     * @throws ApiError
     */
    public static async cancelScheduleTimeOff({
companyId,
scheduleTimeOffId,
}: {
companyId: number,
scheduleTimeOffId: number,
}): Promise<ScheduleTimeOff> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/schedule/time-off/${scheduleTimeOffId}/cancel`,
            errors: {
                404: `No cancellable (pending/accepted) request with that id`,
            },
        });
        return result.body;
    }

}